package nl.volleyplanner.web.util

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger

fun <A, B> StateFlow<A>.combineAndLaunch(coroutineScope: CoroutineScope, other: StateFlow<B>) : StateFlow<Pair<A, B>> {
    return this.combine(other, { a, b -> Pair(a, b)})
        .stateIn(coroutineScope, SharingStarted.Eagerly, Pair(this.value, other.value))
}

fun List<StateFlow<*>>.combineAndLaunch(coroutineScope: CoroutineScope) : StateFlow<List<Any?>> {
    return combine(flows = this) { it.asList().map { it } }
        .stateIn(coroutineScope, SharingStarted.Eagerly, this.map { it.value })
}

/**
 * Stateflow channeling a source StateFlow
 * The source flow can be changed to channel towards a new flow without dispruption of the existing flow
 *
 * @param initialSource Starting source StateFlow to channel from
 */
@OptIn(ExperimentalForInheritanceCoroutinesApi::class)
class SwitchingStateFlow<T>(
    private val coroutineScope: CoroutineScope,
    initialSource: StateFlow<T>
) : StateFlow<T> {
    private val logger = Logger.getLogger("nl.volleyplanner.web.util.StateUtil.SwitchingStateFlow")

    private var currentSource = initialSource
    private val internalState = MutableStateFlow(initialSource.value)

    private var internalJob: Job? = null

    override val replayCache: List<T>
        get() = internalState.replayCache
    override val value: T
        get() = internalState.value

    /**
     * Switch to a new StateFlow to channel from
     * This will emit the current value of the new StateFlow to any active collector
     */
    fun updateSource(newSource: StateFlow<T>) {
        logger.debug("updating source flow")
        if (currentSource != newSource) {
            currentSource = newSource
            internalState.value = currentSource.value
            startCollectionJob()
        }
    }

    override suspend fun collect(collector: FlowCollector<T>): Nothing {
        logger.debug("New collector registered")
        if (internalJob == null || !internalJob!!.isActive) {
            startCollectionJob()
        }
        try {
            internalState.collect(collector)
        } finally {
            if (internalState.subscriptionCount.value == 0) {
                logger.debug("Cancelled collection job")
                internalJob?.cancel()
            }
        }
    }

    private fun startCollectionJob() {
        internalJob?.cancel()
        // TODO: Run within existing coroutine scope for cancellation
        internalJob = coroutineScope.launch {
            logger.debug("Started new collection job")
            if (internalState.value != currentSource.value) {
                internalState.value = currentSource.value
            }
            currentSource.collect { value ->
                internalState.value = value
            }
        }
    }
}